<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import { useBreakpoint } from "@/design/composables/use-breakpoint";
import { useTheme } from "@/design/composables/use-theme";
import type { IconName } from "@/design/icons";
import Button from "@/design/primitives/Button.vue";
import Icon from "@/design/primitives/Icon.vue";
import Tooltip from "@/design/primitives/Tooltip.vue";
import { useCan } from "@/features/auth/api/can";
import CreditsChip from "@/features/billing/components/CreditsChip.vue";
import NotificationBell from "@/features/notifications/components/NotificationBell.vue";
import BenvoxLogo from "./BenvoxLogo.vue";
import { shellMessages } from "./i18n/shell";
import PageTitle from "./PageTitle.vue";
import UserMenu from "./UserMenu.vue";

/**
 * A barra de cima: onde estou, o que procuro, o que chegou, quem eu sou.
 *
 * Saiu do `App.vue` sem uma linha de comportamento mudada. A moldura decidia
 * quatro assuntos e este era um; o que ela guarda de verdade — a sessão, as
 * portas, o teclado, o `<RouterView>` — não cabia junto de um `<svg>`.
 *
 * As duas aberturas viram evento em vez de `ref` compartilhado: o menu e a
 * paleta são desenhados por outra pessoa, e um cabeçalho que escreve no estado
 * de fora é um cabeçalho que só funciona nesta moldura.
 *
 * ── A ORDEM DAS TRÊS ÁREAS, QUE NÃO É ARBITRÁRIA ──────────────────────────
 *
 * Marca à esquerda, busca no meio, eu à direita — é onde a V2 põe cada uma, e
 * onde a mão já vai procurar. Antes o menu da conta morava na PONTA ESQUERDA,
 * colado no hambúrguer: o primeiro elemento da tela era o avatar de quem já sabe
 * quem é, e o produto não se apresentava em lugar nenhum. O logo não é enfeite —
 * é a única coisa que diz, numa aba entre vinte, qual das duas versões está
 * aberta.
 *
 * ── OS SETE ATRIBUTOS DE `<svg>` QUE MORAVAM AQUI ────────────────────────
 *
 * Foram para o `Icon.vue`. Eram uma constante local copiada em quatro arquivos da
 * casca, e a cópia já tinha divergido na espessura do traço — o tipo de diferença
 * que ninguém vê num arquivo e todo mundo sente na tela.
 */
defineEmits<{ openNav: []; openPalette: [] }>();

/**
 * Se há sessão. Vem da moldura, que é quem a restaura — o saldo não se pergunta
 * antes de haver a quem perguntar.
 */
defineProps<{ signedIn: boolean; benAvailable?: boolean }>();

const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: shellMessages });

const { isMobile } = useBreakpoint();
const { resolved, cycle } = useTheme();

/** `/billing` é `minRole: "admin"`, e o chip só vira link para quem entra lá. */
const isAdmin = useCan("admin");

/**
 * `⌘` no Mac, `Ctrl` em todo o resto — lido uma vez, do navegador.
 *
 * `navigator.platform` está depreciado e continua sendo o único sinal que não
 * mente sobre isto; `userAgentData` não existe no Safari, que é justamente o
 * navegador em que a resposta importa. Os dois, nesta ordem, e `Ctrl` como queda:
 * dizer `Ctrl` a um usuário de Mac é um atalho que ele ainda encontra, e dizer
 * `⌘` a um usuário de Windows é uma tecla que não existe no teclado dele.
 */
const modifierKey = computed(() => {
	if (typeof navigator === "undefined") return "Ctrl";
	const hint = `${(navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform ?? ""} ${navigator.platform ?? ""} ${navigator.userAgent ?? ""}`;
	return /mac|iphone|ipad|ipod/i.test(hint) ? "⌘" : "Ctrl";
});

/**
 * O tema DIZ qual é, em vez de escrever o nome dele.
 *
 * O botão mostrava `tema: light` — o valor cru, em inglês, numa tela em
 * português, e ainda ocupando a largura de duas palavras. Um sol, uma lua e uma
 * tela respondem a mesma pergunta sem texto nenhum; o nome continua existindo,
 * onde texto não custa lugar, que é o `aria-label` e a dica.
 */
const THEME: Record<"light" | "dark", { icon: IconName; label: string; next: string }> = {
	light: { icon: "sun", label: "light", next: "dark" },
	dark: { icon: "moon", label: "dark", next: "light" },
};

/**
 * O BOTÃO DE ÍCONE DESTA BARRA — 36×36, raio de 8px e traço de 18px.
 *
 * São as medidas de `.topbar-btn` na V2, e não as do `Button size="icon"`
 * genérico (32×32 com traço de 16px), que é o que a barra desenhava. Quatro
 * pixels de caixa e dois de traço parecem nada isolados; lado a lado com a V2
 * eram a barra inteira parecendo menor. A cor também é a de lá: cinza médio em
 * repouso, quase preto sobre um cinza claríssimo no hover (`bar-*` em
 * `tokens.css`).
 *
 * Aplicado por classe e não mexendo no primitivo: `Button` é usado em cada tela
 * do produto, e um `size="icon"` de 36px mudaria toda barra de ferramentas do
 * painel para consertar esta.
 */
// padrão Claude (dono, 25/10): redondo e calado; 44px no telefone (ver `MOBILE_MENU`).
const BAR_BTN = "size-10 rounded-full text-bar-fg hover:bg-bar-hover hover:text-bar-fg-strong";

/**
 * O ☰ do telefone, do mesmo peso dos outros ícones da barra (dono, 25/10: "melhore a
 * topbar"). A bolha com borda do Ben é para FLUTUAR sobre a conversa; aqui a barra já
 * é o fundo, e o círculo só pesava no canto.
 */
const MOBILE_MENU = "size-10 rounded-full text-bar-fg-strong hover:bg-bar-hover";

const theme = computed(() => {
	const row = THEME[resolved.value];
	return {
		icon: row.icon,
		name: t(`header.themes.${row.label}`),
		next: t(`header.themes.${row.next}`),
	};
});
</script>

<template>
	<!-- A BARRA, COM A COR QUE A V2 LHE DÁ.
	     `bg-white/80 dark:bg-neutral-900/80` com desfoque e a borda a 80% — é o que
	     o `TopBar.vue` da V2 escreve, e é o que os tokens `bar-*` guardam. Em
	     repouso, sobre a página, isso dá praticamente o branco de antes; o que muda
	     de verdade é o ESCURO, onde a V2 pinta `#171717` e a `surface` da V3 pintava
	     `#212121` — a barra saía mais clara que tudo à volta. -->
	<!-- padrão Claude (dono, 25/10): translúcida, com um fio fino embaixo — a barra tem a cor da página e, sem ele, o conteúdo sobe sem limite. -->
	<header class="shrink-0 border-b border-bar-line bg-bar-bg backdrop-blur-md">
		<div class="flex h-14 items-center gap-2 px-4 sm:gap-3">
			<div class="flex min-w-0 shrink items-center gap-1">
				<Button
					v-if="isMobile"
					variant="ghost"
					size="icon"
					:class="MOBILE_MENU"
					:aria-label="t('header.openNav')"
					@click="$emit('openNav')"
				>
					<Icon name="menu" class="size-5" />
				</Button>
				<PageTitle v-if="isMobile" />
				<!-- O alfinete do menu NÃO está aqui. Ele mora na primeira linha da
				     própria coluna, à direita, que é onde a V2 o desenha nos três menus
				     que tem — o comentário que o trouxe para cá afirmava o contrário.
				     Um controle de coluna entre o hambúrguer e a marca fica no único
				     canto da tela que não fala da coluna. Ver `AppRail.vue`. -->
				<!-- Some no telefone, como na V2: 986 unidades de largura de marca numa
				     tela de 360px seriam a metade da barra dizendo algo que quem já
				     entrou sabe. A porta para o início continua no menu. -->
				<RouterLink
					to="/"
					:aria-label="t('header.home')"
					class="hidden items-center rounded-sm text-fg transition-opacity hover:opacity-80 md:flex"
				>
					<!-- 28px numa barra de 56px: metade da altura útil, que é a proporção
					     da V2. A 20px a marca virava um selo de rodapé no alto da tela —
					     a queixa do dono, e a única medida que faltava aqui. -->
					<BenvoxLogo class="h-7 w-auto" />
				</RouterLink>
			</div>

			<!-- A busca, no meio e com cara de campo.
			     Ela é a porta mais larga do painel — alcança as vinte telas, as
			     entidades do tenant e as ações — e um atalho de teclado escrito num
			     botão só é entendido por quem já o conhecia. Com a moldura, a lupa e a
			     palavra, ela se parece com o que é; o `⌘K` fica ao lado como o que ele
			     é, um atalho, e não como o nome do controle. Estreito, tudo isso é
			     largura que a barra não tem: sobra a lupa. -->
			<div class="flex min-w-0 flex-1 justify-center max-md:justify-end">
				<button
					type="button"
					:aria-label="t('header.search')"
					class="flex size-11 items-center justify-center gap-2 rounded-full text-sm text-fg-subtle transition-colors hover:bg-bar-hover hover:text-fg max-md:ml-auto sm:h-10 sm:w-full sm:max-w-lg sm:justify-start sm:border sm:border-border sm:bg-surface-raised sm:pl-4 sm:pr-2 sm:hover:border-border-strong sm:hover:bg-surface-raised"
					@click="$emit('openPalette')"
				>
					<Icon name="search" class="size-4 shrink-0" />
					<span class="hidden sm:inline">{{ t("header.search") }}</span>
					<!--
						DUAS teclas, e a primeira depende de onde a pessoa está sentada.
						Estava escrito `⌘K` fixo, e o ⌘ não existe em Windows nem em Linux —
						que é onde está a maioria de quem usa o painel. O atalho sempre
						aceitou as duas (`metaKey || ctrlKey`, em `App.vue`); só o desenho
						contava uma das duas metades.
					-->
					<span class="ml-auto hidden items-center gap-0.5 sm:flex">
						<kbd
							class="rounded-md bg-bg-muted px-1.5 py-0.5 font-mono text-3xs text-fg-subtle"
						>
							{{ modifierKey }}
						</kbd>
						<kbd
							class="rounded-md bg-bg-muted px-1.5 py-0.5 font-mono text-3xs text-fg-subtle"
						>
							K
						</kbd>
					</span>
				</button>
			</div>

			<div class="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
				<!-- O saldo, antes do sino: o aviso que ninguém pediu vem depois do
				     número que decide se a IA responde hoje. -->
				<div class="hidden sm:block">
					<CreditsChip :enabled="signedIn" :can-open="isAdmin" />
				</div>
				<div v-if="benAvailable" id="ben-launcher" />
				<NotificationBell />
				<!-- No telefone o tema mora no menu do avatar: cinco botões não cabem. -->
				<Tooltip v-if="!isMobile" :text="t('header.themeNext', { name: theme.name, next: theme.next })">
					<Button
						variant="ghost"
						size="icon"
						:class="BAR_BTN"
						:aria-label="t('header.theme', { name: theme.name })"
						@click="cycle"
					>
						<Icon :name="theme.icon" class="size-[18px]" />
					</Button>
				</Tooltip>
				<!-- O traço que separa o que é da FERRAMENTA do que é MEU. Sem ele, o
				     avatar vira mais um ícone numa fila de ícones. -->
				<span class="mx-1 hidden h-5 w-px bg-bar-line sm:block" />
				<UserMenu />
			</div>
		</div>
	</header>
</template>
